import React, { useEffect, useState } from "react";
import { getKnowledgePins, unpinKnowledgeNote } from "../api";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";

const Pinboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPins = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getKnowledgePins();
      setPins(res.data.data || []);
    } catch (e) {
      console.error("Failed to load pins", e);
      setError("Failed to load pins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPins();
  }, []);

  const handleUnpin = async (noteId) => {
    try {
      await unpinKnowledgeNote(noteId);
      await loadPins();
    } catch (e) {
      console.error("Failed to unpin note", e);
      setError("Failed to unpin note");
    }
  };

  const handleOpenNote = (pin) => {
    if (pin.folder && pin.folder.id) {
      const params = new URLSearchParams();
      params.set("folderId", pin.folder.id);
      params.set("noteId", pin.note_id);
      navigate(`/knowledge-hub?${params.toString()}`);
    } else {
      navigate("/knowledge-hub");
    }
  };

  const goToFolders = () => navigate("/knowledge-hub");

  return (
    <div
      style={{
        padding: "32px 24px",
        maxWidth: "960px",
        margin: "0 auto",
        color: theme.text,
        minHeight: "60vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" }}>
            Knowledge Hub
          </h1>
          <p style={{ margin: "4px 0 0", color: theme.textSecondary, fontSize: 14 }}>
            Your pinned notes and quick access to folders.
          </p>
        </div>
        {pins.length > 0 && (
          <button
            onClick={goToFolders}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              backgroundColor: theme.primary,
              color: "#fff",
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 500,
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            Go to folders & notes
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: 16,
            marginBottom: 0,
            padding: "10px 14px",
            borderRadius: 8,
            backgroundColor: "rgba(200, 50, 50, 0.1)",
            color: "#c92a2a",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {loading && pins.length === 0 ? (
        <div
          style={{
            color: theme.textSecondary,
            fontSize: 14,
            marginTop: 32,
            textAlign: "center",
          }}
        >
          Loading…
        </div>
      ) : pins.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
            marginTop: -32,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: theme.backgroundSecondary,
              border: `2px solid ${theme.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 32,
              marginBottom: 20,
            }}
          >
            📌
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 600,
              color: theme.text,
              letterSpacing: "-0.01em",
            }}
          >
            No pinned notes yet
          </h2>
          <p
            style={{
              margin: "8px 0 24px",
              color: theme.textSecondary,
              fontSize: 15,
              maxWidth: 360,
              lineHeight: 1.5,
            }}
          >
            Pin notes from folders to see them here for quick access. Go to folders and notes to get started.
          </p>
          <button
            onClick={goToFolders}
            style={{
              padding: "12px 24px",
              borderRadius: 8,
              border: "none",
              backgroundColor: theme.primary,
              color: "#fff",
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
          >
            Go to folders & notes
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 20,
            marginTop: 24,
          }}
        >
          {pins.map((pin) => (
            <div
              key={`${pin.user_id}-${pin.sort_key}`}
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: 12,
                padding: 16,
                backgroundColor: theme.cardBackground,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {pin.note?.title || "Untitled note"}
              </div>
              {pin.folder && (
                <div
                  style={{
                    fontSize: 12,
                    color: theme.textSecondary,
                    marginBottom: 6,
                  }}
                >
                  Folder: {pin.folder.name}
                </div>
              )}
              {pin.note?.content_html && (
                <div
                  style={{
                    fontSize: 13,
                    color: theme.textSecondary,
                    marginBottom: 8,
                    maxHeight: 72,
                    overflow: "hidden",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: pin.note.content_html,
                  }}
                />
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <button
                  onClick={() => handleOpenNote(pin)}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: theme.primary,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Open
                </button>
                <button
                  onClick={() => handleUnpin(pin.note_id)}
                  title="Unpin"
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: "transparent",
                    color: theme.primary,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Unpin
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Pinboard;

